import {
  ArrowUp,
  Check,
  ChevronLeft,
  Loader2,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { client, type VoiceCapabilities } from '../../../../api/client';
import { mapVoiceViolations } from '../../../lib/voiceErrors';

interface Props {
  onBack: () => void;
  onReady: (file: File) => void;
  onSubmit: () => void;
  submitting: boolean;
}

const DEFAULT_HINT = 'MP3, WAV, OGG, M4A — до 50 МБ';

const mb = (bytes: number) => Math.round(bytes / 1024 / 1024);

export function StepVoiceUpload({ onBack, onReady, onSubmit, submitting }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const validation = useRef<AbortController | null>(null);
  const [cap, setCap] = useState<VoiceCapabilities | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [valid, setValid] = useState(false);
  const [checking, setChecking] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const c = new AbortController();

    client.voices
      .capabilities(c.signal)
      .then(setCap)
      .catch(() => {
        if (!c.signal.aborted) setError('Не удалось загрузить требования к файлу.');
      });

    return () => {
      c.abort();
      validation.current?.abort();
    };
  }, []);

  const choose = async (next: File) => {
    validation.current?.abort();
    const controller = new AbortController();
    validation.current = controller;

    setFile(next);
    setValid(false);
    setChecking(false);
    setError('');

    if (cap) {
      const ext = next.name.split('.').pop()?.toLowerCase();

      if (!ext || !cap.allowedFormats.includes(ext as never)) {
        setError(`Поддерживаются форматы ${cap.allowedFormats.join(', ').toUpperCase()}`);
        return;
      }

      if (next.size > cap.maxSizeBytes) {
        setError(`Максимальный размер файла — ${mb(cap.maxSizeBytes)} МБ`);
        return;
      }
    }

    try {
      setChecking(true);
      const result = await client.voices.validate(next, controller.signal);
      if (controller.signal.aborted) return;

      const errors = mapVoiceViolations(result.violations);
      if (errors.length) {
        setError(errors.join(' '));
        return;
      }

      setValid(true);
      onReady(next);
    } catch (e: any) {
      if (controller.signal.aborted) return;

      const violations = e?.body?.detail?.violations;
      setError(
        Array.isArray(violations)
          ? mapVoiceViolations(violations).join(' ')
          : 'Не удалось проверить файл.',
      );
    } finally {
      if (!controller.signal.aborted) setChecking(false);
    }
  };

  const openPicker = () => input.current?.click();

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped && !checking && !submitting) void choose(dropped);
  };

  const hint = cap
    ? `${cap.allowedFormats.join(', ').toUpperCase()} — до ${mb(cap.maxSizeBytes)} МБ`
    : DEFAULT_HINT;

  const state = checking ? 'checking' : error ? 'error' : valid ? 'success' : 'idle';

  return (
    <motion.div
      key="voice"
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className="w-full max-w-[640px]"
    >
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
      >
        <ChevronLeft className="h-4 w-4" />
        Назад
      </button>

      <h2 className="mt-6 text-center text-3xl leading-9 font-bold tracking-tight text-violet-600">
        Загрузите ваш голос
      </h2>

      <div className="mt-5 rounded-2xl bg-white p-5 shadow">
        <div className="flex items-center gap-2">
          <span className="text-2xl leading-8 tracking-tight" aria-hidden="true">
            🎙️
          </span>
          <span className="text-lg leading-7 font-bold text-slate-800">
            Загрузите свой голос
          </span>
        </div>

        <input
          ref={input}
          className="hidden"
          type="file"
          accept={cap?.allowedFormats.map((x) => `.${x}`).join(',')}
          onChange={(e) => {
            const picked = e.target.files?.[0];
            e.target.value = '';
            if (picked) void choose(picked);
          }}
        />

        <div aria-live="polite">
          {(state === 'idle' || state === 'checking') && (
            <>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                className={`mt-4 flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed p-8 transition-colors ${
                  dragging
                    ? 'border-violet-400 bg-purple-100'
                    : 'border-purple-300 bg-purple-50'
                }`}
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-violet-100 to-pink-100">
                  {state === 'checking' ? (
                    <Loader2 className="h-7 w-7 animate-spin text-violet-600" strokeWidth={2.5} />
                  ) : (
                    <ArrowUp className="h-7 w-7 text-violet-600" strokeWidth={2.5} />
                  )}
                </div>

                <div className="w-56 max-w-full text-center">
                  <p className="text-base leading-6 font-semibold text-gray-700">
                    {state === 'checking' ? 'Проверяем файл…' : 'Перетащите файл сюда'}
                  </p>
                  <p className="truncate pt-1 text-sm leading-5 text-gray-400">
                    {state === 'checking' && file ? file.name : hint}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-sm leading-5 text-gray-400">или</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>

              <button
                type="button"
                onClick={openPicker}
                disabled={checking || submitting}
                className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-violet-600 to-purple-500 text-base leading-6 font-semibold text-white transition-shadow hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Upload className="h-5 w-5" />
                Выбрать файл голоса
              </button>
            </>
          )}

          {state === 'success' && (
            <div className="mt-4 flex flex-col items-center gap-3 rounded-2xl border-2 border-green-300 bg-green-50 p-6">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-500 shadow-md">
                <Check className="h-7 w-7 text-white" strokeWidth={2.5} />
              </div>

              <div className="max-w-full text-center">
                <p className="text-base leading-6 font-bold text-green-700">
                  Голос успешно загружен!
                </p>
                {file && (
                  <p className="mx-auto max-w-[13rem] truncate pt-1 text-sm leading-5 text-green-600">
                    {file.name}
                  </p>
                )}
              </div>

              <div className="h-1.5 w-64 max-w-full overflow-hidden rounded-full bg-green-100">
                <div className="h-full w-full rounded-full bg-emerald-500" />
              </div>

              <button
                type="button"
                onClick={openPicker}
                disabled={submitting}
                className="text-sm leading-5 text-green-600 underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                Загрузить другой файл
              </button>
            </div>
          )}

          {state === 'error' && (
            <div
              role="alert"
              className="mt-4 flex flex-col items-center gap-3 rounded-2xl border-2 border-red-300 bg-red-50 p-6"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500 shadow-md">
                <X className="h-7 w-7 text-white" strokeWidth={2.5} />
              </div>

              <div className="max-w-full text-center">
                <p className="text-base leading-6 font-bold text-red-700">Ошибка загрузки</p>
                <p className="mx-auto max-w-[16rem] pt-1 text-sm leading-5 text-red-500">
                  {error}
                </p>
              </div>

              <button
                type="button"
                onClick={openPicker}
                className="rounded-xl bg-gradient-to-br from-red-500 to-orange-500 px-5 py-2 text-sm leading-5 font-semibold text-white transition-shadow hover:shadow-md"
              >
                Попробовать снова
              </button>
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={onSubmit}
        disabled={!valid || submitting}
        className="mt-6 flex h-14 w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-br from-violet-600 to-pink-500 text-lg leading-7 font-bold text-white shadow-lg transition-shadow hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Sparkles className="h-5 w-5" />
        {submitting ? 'Подготавливаем голос…' : 'Создать песню'}
      </button>
    </motion.div>
  );
}
